import React, { useState } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

import breakpoints from "_constants/breakpoints";

import { ReactComponent as ArrowDownSVG } from "assets/img/ArrowDown.svg";

import PhoneMask from "containers/PhoneMask";
import { phoneCleaner } from "_helpers";
import Pagination from "components/Pagination/Pagination";

const Content = styled.div`
  min-width: 100%;
  max-width: 100%;
  background: #ffffff;
  box-shadow: 0px 3px 25px rgba(168, 168, 168, 0.3);
  width: 100%;
  height: 0;
  opacity: 0;
  transform: scaleY(0);
  transform-origin: 0 0;
  transition: 0.5s ease-in-out;
  ${({ isOpen }) =>
    isOpen
      ? css`
          height: 100%;
          opacity: 1;
          transform: scaleY(1);
          margin-bottom: 33px;
          padding: 24px;

          @media (max-width: ${breakpoints.mobileL}) {
            padding: 24px 16px;
          }
        `
      : css`
          overflow-y: hidden;
        `}
`;
const SectionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: #ffffff;
  border: 1px solid #a5aaaf;
  box-sizing: border-box;
  padding: 8px 15px 8px 20px;
  margin-bottom: 10px;
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 36px;
  text-align: left;
  color: #03589c;
  transform-origin: 0 0;
  transition: 0.5s ease-in-out;
  ${({ isOpen }) =>
    isOpen &&
    css`
      margin-bottom: 24px;
    `}

  @media (max-width: ${breakpoints.mobileL}) {
    line-height: 24px;
  }
`;
const Row = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid #ecedf1;
  &:last-child {
    border-bottom: 1px solid #ecedf1;
  }
`;
const DataRow = styled.div`
  display: flex;
  min-width: 100%;
  padding: 16px 0;

  &:nth-child(2n + 1) {
    background-color: #fafaff;
  }

  @media (max-width: ${breakpoints.tablet}) {
    flex-direction: column;
  }
`;
const Label = styled.div`
  min-width: 50%;
  width: 50%;
  padding-right: 20px;
  font-weight: normal;
  font-size: 12px;
  text-transform: uppercase;
  color: #7b848d;
  word-break: break-all;

  @media (max-width: ${breakpoints.tablet}) {
    min-width: 100%;
    width: 100%;
    padding-right: 0;
  }
`;
const Data = styled(Label)`
  font-weight: bold;
  text-transform: none;
  word-break: break-all;

  @media (max-width: ${breakpoints.mobileL}) {
    align-items: center;
  }
`;
const Phone = styled(PhoneMask)`
  border: none;
  padding: 0;
  font-family: "Montserrat";
  font-size: 12px;
  white-space: nowrap;
  color: #7b848d;
  background: none;
`;
const EmptyMessage = styled.div`
  color: #7b848d;
  font-weight: bold;
  text-align: center;
`;

function TableRow({ index, ...props }) {
  return (
    <Row index={index}>
      <DataRow>
        <Label>Mark Name</Label>
        <Data>{props?.markName || "—"}</Data>
      </DataRow>

      <DataRow>
        <Label>Number Assigned</Label>
        <Data>{props?.numberAssigned || "—"}</Data>
      </DataRow>

      <DataRow>
        <Label>First Name</Label>
        <Data>{props?.owner?.firstName || "—"}</Data>
      </DataRow>

      <DataRow>
        <Label>Middle Name</Label>
        <Data>{props?.owner?.middleName || "—"}</Data>
      </DataRow>

      <DataRow>
        <Label>Last Name</Label>
        <Data>{props?.owner?.lastName || "—"}</Data>
      </DataRow>

      <DataRow>
        <Label>Suffix Name</Label>
        <Data>{props?.owner?.suffixName || "—"}</Data>
      </DataRow>

      <DataRow>
        <Label>Phone</Label>
        <Data>
          <Phone mask="(999) 999-9999" value={phoneCleaner(props?.owner?.phone) || "—"} />
        </Data>
      </DataRow>

      <DataRow>
        <Label>Email</Label>
        <Data>{props?.owner?.email || "—"}</Data>
      </DataRow>

      <DataRow>
        <Label>Country of Citizenship</Label>
        <Data>{props?.owner?.countryOfCitizenship || "—"}</Data>
      </DataRow>

      <DataRow>
        <Label>Address 1</Label>
        <Data>{props?.owner?.address?.address1 || "—"}</Data>
      </DataRow>

      <DataRow>
        <Label>Address 2</Label>
        <Data>{props?.owner?.address?.address2 || "—"}</Data>
      </DataRow>

      <DataRow>
        <Label>City</Label>
        <Data>{props?.owner?.address?.city || "—"}</Data>
      </DataRow>

      <DataRow>
        <Label>State</Label>
        <Data>{props?.owner?.address?.state || "—"}</Data>
      </DataRow>

      <DataRow>
        <Label>Zip</Label>
        <Data>{props?.owner?.address?.zip || "—"}</Data>
      </DataRow>

      <DataRow>
        <Label>County</Label>
        <Data>{props?.owner?.address?.county || "—"}</Data>
      </DataRow>

      <DataRow>
        <Label>Classification</Label>
        <Data>{props?.classification || "—"}</Data>
      </DataRow>

      <DataRow>
        <Label>Service</Label>
        <Data>{props?.service || "—"}</Data>
      </DataRow>

      <DataRow>
        <Label>Description</Label>
        <Data>{props?.description || "—"}</Data>
      </DataRow>

      <DataRow>
        <Label>Renewal Date</Label>
        <Data>{props?.renewalDate || "—"}</Data>
      </DataRow>
    </Row>
  );
}

TableRow.propTypes = {
  index: PropTypes.number,
  markName: PropTypes.string,
  numberAssigned: PropTypes.string,
  owner: PropTypes.object,
  classification: PropTypes.string,
  service: PropTypes.string,
  description: PropTypes.string,
  renewalDate: PropTypes.string,
};

function StateSearchResults({ stateSearchResults }) {
  const [isOpen, setOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = stateSearchResults?.length || 0;

  return (
    <>
      <SectionButton id="state-search-results" isOpen={isOpen} onClick={() => setOpen(!isOpen)}>
        State Search Results
        <ArrowDownSVG fill="#F37217" transform={isOpen ? "rotate(180)" : undefined} />
      </SectionButton>

      <Content isOpen={isOpen}>
        {stateSearchResults.length ? (
          <>
            <Pagination top currentPage={currentPage} totalPages={totalPages} onClick={setCurrentPage} />
            <TableRow {...stateSearchResults[currentPage - 1]} />
          </>
        ) : (
          // ))
          <EmptyMessage>No conflicting State search results found</EmptyMessage>
        )}
      </Content>
    </>
  );
}

StateSearchResults.propTypes = {
  stateSearchResults: PropTypes.arrayOf(PropTypes.object),
};

export default StateSearchResults;
